import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './ConversationPage.css';

function ConversationPage({ apiUrl, wsUrl }) {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [status, setStatus] = useState('idle'); // idle, listening, thinking, speaking
  const [transcript, setTranscript] = useState({ user: '', assistant: '' });
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);
  
  const wsRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioBufferRef = useRef([]);
  const mediaRecorderRef = useRef(null);
  const recordingIntervalRef = useRef(null);
  const startRecordingRef = useRef(null);
  const audioChunksRef = useRef([]);

  useEffect(() => {
    let mounted = true;
    
    const setup = async () => {
      try {
        await initializeAudio();
        if (mounted) {
          connectWebSocket();
        }
      } catch (err) {
        console.error('Setup error:', err);
        if (mounted) {
          setError('Failed to initialize: ' + err.message);
        }
      }
    };
    
    setup();
    
    return () => {
      mounted = false;
      cleanup();
    };
  }, [profileId, token]);

  const initializeAudio = async () => {
    try {
      // Check if getUserMedia is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        // Check for legacy APIs
        const getUserMedia = navigator.getUserMedia ||
                            navigator.webkitGetUserMedia ||
                            navigator.mozGetUserMedia ||
                            navigator.msGetUserMedia;
        
        if (!getUserMedia) {
          const errorMsg = 'Microphone access is not available. This may be because:\n' +
                          '1. The page is not served over HTTPS (required for remote access)\n' +
                          '2. Your browser does not support getUserMedia\n' +
                          '3. Microphone permissions are blocked\n\n' +
                          'Please try:\n' +
                          '- Using HTTPS (https://109.123.227.149:3005)\n' +
                          '- Or accessing from localhost if testing locally\n' +
                          '- Or check browser permissions';
          throw new Error(errorMsg);
        }
        
        // For older browsers, wrap in Promise
        const legacyStream = await new Promise((resolve, reject) => {
          getUserMedia.call(navigator, { audio: true }, resolve, reject);
        });
        mediaStreamRef.current = legacyStream;
      } else {
        // Modern browser API
        const modernStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        mediaStreamRef.current = modernStream;
      }
      
      // Get the stream (already stored in mediaStreamRef.current)
      const stream = mediaStreamRef.current;
      
      // Create audio context for playback
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      
      // Start capturing audio
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      // Reset audio chunks ref
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          console.log(`Audio chunk received: ${event.data.size} bytes`);
          audioChunksRef.current.push(event.data);
        } else {
          console.warn('Received empty audio chunk');
        }
      };
      
      mediaRecorder.onstop = () => {
        // When recording stops, combine chunks into a complete WebM file
        // Use setTimeout to ensure all data is flushed before creating blob
        setTimeout(() => {
          if (audioChunksRef.current.length > 0 && wsRef.current?.readyState === WebSocket.OPEN) {
            try {
              const completeBlob = new Blob(audioChunksRef.current, { type: 'audio/webm;codecs=opus' });
              // Log blob size for debugging
              console.log(`Sending audio blob: ${completeBlob.size} bytes, ${audioChunksRef.current.length} chunks`);
              wsRef.current.send(completeBlob);
              audioChunksRef.current = []; // Clear for next recording
            } catch (err) {
              console.error('Error sending audio:', err);
            }
          }
        }, 100); // Small delay to ensure MediaRecorder has fully stopped
      };
      
      // Start recording and stop after 2 seconds to create complete segments
      const startRecording = () => {
        console.log(`startRecording called: mediaRecorder.state=${mediaRecorder?.state}, wsReady=${wsRef.current?.readyState}, wsExists=${!!wsRef.current}`);
        if (!mediaRecorder) {
          console.error('MediaRecorder is null!');
          return;
        }
        if (!wsRef.current) {
          console.error('WebSocket ref is null!');
          return;
        }
        if (mediaRecorder.state === 'inactive' && wsRef.current.readyState === WebSocket.OPEN) {
          audioChunksRef.current = [];
          console.log('Starting MediaRecorder...');
          try {
            mediaRecorder.start(100); // Request data every 100ms
            console.log(`MediaRecorder started, state: ${mediaRecorder.state}`);
            // Stop after 2 seconds to create a complete WebM segment
            recordingIntervalRef.current = setTimeout(() => {
              if (mediaRecorder.state === 'recording') {
                console.log('Stopping MediaRecorder, chunks collected:', audioChunksRef.current.length);
                // Request all available data before stopping to ensure complete file
                try {
                  mediaRecorder.requestData();
                } catch (e) {
                  console.warn('requestData failed:', e);
                }
                // Small delay to let requestData process
                setTimeout(() => {
                  if (mediaRecorder.state === 'recording') {
                    mediaRecorder.stop();
                    // Immediately start next recording if WebSocket is still open
                    if (wsRef.current?.readyState === WebSocket.OPEN) {
                      setTimeout(startRecording, 100);
                    }
                  }
                }, 50);
              } else {
                console.warn(`MediaRecorder not recording, state: ${mediaRecorder.state}`);
              }
            }, 2000);
          } catch (err) {
            console.error('Error starting MediaRecorder:', err);
          }
        } else {
          console.warn(`Cannot start recording: state=${mediaRecorder.state}, wsReady=${wsRef.current?.readyState}`);
        }
      };
      
      // Store startRecording function so it can be called when WebSocket opens
      startRecordingRef.current = startRecording;
      
      // Store mediaRecorder for cleanup
      mediaRecorderRef.current = mediaRecorder;
      
    } catch (err) {
      console.error('Audio initialization error:', err);
      setError('Failed to access microphone: ' + err.message);
      throw err;
    }
  };

  const connectWebSocket = () => {
    // Close existing connection if any
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      wsRef.current.close();
    }
    
    const sessionId = `${profileId}_${Date.now()}`;
    const tokenParam = token ? `?token=${encodeURIComponent(token)}` : '';
    const wsUrlFull = wsUrl.includes('/ws') 
      ? `${wsUrl}/conversation/${sessionId}${tokenParam}`
      : `${wsUrl}/ws/conversation/${sessionId}${tokenParam}`;
    console.log('Connecting to WebSocket:', wsUrlFull);
    
    const ws = new WebSocket(wsUrlFull);
    
    ws.onopen = () => {
      console.log('WebSocket connected');
      setConnected(true);
      setStatus('listening');
      setError(null);
      // Start recording now that WebSocket is connected
      if (startRecordingRef.current) {
        console.log('Starting recording after WebSocket connection...');
        startRecordingRef.current();
      } else {
        console.warn('startRecording function not available yet');
      }
    };
    
    ws.onmessage = async (event) => {
      console.log('WebSocket message received:', event.data instanceof Blob ? 'Blob' : event.data);
      if (event.data instanceof Blob) {
        // Audio data
        await playAudio(event.data);
        setStatus('speaking');
        setTimeout(() => setStatus('listening'), 1000);
      } else {
        // JSON data
        try {
          const data = JSON.parse(event.data);
          console.log('Parsed message:', data);
          
          if (data.type === 'transcript') {
            setTranscript({
              user: data.user || '',
              assistant: data.assistant || ''
            });
            setStatus('thinking');
          } else if (data.type === 'audio_ready') {
            setStatus('speaking');
          }
        } catch (err) {
          console.error('Error parsing message:', err);
        }
      }
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setError('WebSocket error occurred');
      setConnected(false);
    };
    
    ws.onclose = (event) => {
      console.log('WebSocket closed:', event.code, event.reason);
      setConnected(false);
      setStatus('idle');
      // Don't set error on normal close
      if (event.code !== 1000 && event.code !== 1001) {
        setError(`Connection closed: ${event.reason || 'Unknown reason'}`);
      }
    };
    
    wsRef.current = ws;
  };

  const playAudio = async (audioBlob) => {
    try {
      const arrayBuffer = await audioBlob.arrayBuffer();
      const audioContext = audioContextRef.current;
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContext.destination);
      source.start();
    } catch (err) {
      console.error('Error playing audio:', err);
    }
  };

  const cleanup = () => {
    console.log('Cleaning up...');
    // Clear recording interval
    if (recordingIntervalRef.current) {
      clearTimeout(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
    // Stop media recorder
    if (mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    }
    // Stop media stream
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
    }
    // Close WebSocket
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      wsRef.current.close(1000, 'Cleanup');
    }
  };

  const handleEndConversation = () => {
    if (wsRef.current) {
      wsRef.current.send(JSON.stringify({ type: 'end_conversation' }));
    }
    cleanup();
    navigate('/personalization');
  };

  return (
    <div className="conversation-page">
      <div className="container">
        <h1>Voice Conversation</h1>
        
        {error && <div className="error-message">{error}</div>}
        
        <div className="status-display">
          <span className={`status-indicator status-${status}`}></span>
          <span className="status-text">
            {status === 'idle' && 'Ready to start'}
            {status === 'listening' && 'Listening...'}
            {status === 'thinking' && 'Thinking...'}
            {status === 'speaking' && 'Speaking...'}
          </span>
        </div>
        
        <div className="transcript-display">
          {transcript.user && (
            <div className="transcript-user">
              <strong>You:</strong> {transcript.user}
            </div>
          )}
          {transcript.assistant && (
            <div className="transcript-assistant">
              <strong>AI:</strong> {transcript.assistant}
            </div>
          )}
          {!transcript.user && !transcript.assistant && (
            <p style={{ color: 'var(--text-muted)' }}>Start speaking to begin the conversation...</p>
          )}
        </div>
        
        <div className="button-group">
          <button
            className="button-danger"
            onClick={handleEndConversation}
          >
            End Conversation
          </button>
        </div>
        
        {!connected && (
          <div className="error-message">
            Not connected. Please refresh the page.
          </div>
        )}
      </div>
    </div>
  );
}

export default ConversationPage;



